import axios from 'axios'
import {useEffect, useState} from 'react'
import {Card} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'
import {PageTitle} from 'src/_metronic/layout/core'
import {useNotificationFetcher} from 'src/hooks/useNotificationFetcher'
import {useAppDispatch, useAppSelector} from 'src/redux/hooks'
import {
  TNotification,
  notificationSelector,
  setAmount,
  setIsError,
  setIsLoading,
  setNotification,
  setNotifications,
  setPagination,
  setSearchParams,
  setUnreadNotifications,
} from 'src/redux/slices/notificationSlices'
import {objToParams} from 'src/utils'
import styled from 'styled-components'
import swal from 'sweetalert'
import {fetchNotification, headers, markAllNotificationAsRead} from '../../../services/fetcher'
import Pagination from '../component/Pagination'
import {ListItem} from './ListItem'
import {SearchInput} from './SearchInput'
import {EmptyListSection} from './EmptyListSection'
import {setItemNego} from 'src/_metronic/redux/content'

const StyledCard = styled(Card)`
  height: auto;
  height: calc(100vh - 180px);
  overflow: hidden;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`

const StyledList = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
`

const StyledContent = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 1rem;
  padding: 1rem;
  min-height: 70px;
  border-bottom: 1px solid #e2e2e2;
`

const StyledBody = styled.div`
  flex: 1;
  padding: 1rem;
  background-color: #f1f1f1;
  overflow-y: auto;
`

const StyledPagination = styled.div`
  padding: 1rem 0;
  border-top: 1px solid #f0f0f0;
`

export default function NotificationsPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {notifications, pagination, amount, searchParams, unreadNotifications} = useAppSelector(notificationSelector)
  const {handleFetchNotifications, handleFetchUnreadNotifications} = useNotificationFetcher()

  const paginateNumber = async (url: string) => {
    try {
      if (!url) return

      dispatch(setIsLoading(true))

      const credentials = localStorage.getItem('credential') as string
      const token = localStorage.getItem('token') as string
      const endpoint = `${url}&${objToParams({credentials, ...searchParams})}`
      const res = await axios.get(endpoint, {
        headers: {...headers, Authorization: `Bearer ${token}`},
      })

      const {data, ...rest} = res.data.data
      dispatch(setNotifications(data as TNotification[]))
      dispatch(setPagination(rest))
    } catch (error: any) {
      dispatch(setIsError(error.message))
    } finally {
      dispatch(setIsLoading(false))
    }
  }

  const handleListItemClick = async (item: TNotification) => {
    try {
      dispatch(setNotification(item))
      dispatch(setAmount(amount - 1))

      // Reqeuest notification detail as mark the notification to be read
      await fetchNotification(item.id)

      handleFetchNotifications()

      switch (item.type) {
        // Redirect kehalaman detail findoc
        case 'findoc':
          const id = (item.parent_id || item.child_id)?.toString() ?? ''
          sessionStorage.setItem('id', id)
          navigate('/financial/detail')
          break

        // Redirect kehalaman negosiasi detail (popup)
        case 'negotiation':
          dispatch(
            setItemNego({
              id: item.parent_id,
              type: item.flag_nego === 'header' ? 'multiple' : 'single',
              url: '/management/order/all',
              flag: item.flag_nego,
              customer_id: item.user?.customer_code ? item.user?.customer_code : item.user?.id,
              header_id: item.header_id || null,
              parent_id: item.parent_id,
              role_flag: item.role_flag,
            })
          )
          navigate('/negotiate')
          break

        // Redirect kehalaman orders, lalu buka popup chat untuk order tesebut
        case 'order_sub_header':
          let headerId = parseInt(item.parent_id.substring(2)).toString();
          sessionStorage.setItem('po', headerId)
          navigate('/management/order/detail/' + headerId)
          break

        // Redirect kehalaman orders, lalu buka popup order-item untuk order tesebut
        case 'order_submission':
          let orderId = parseInt(item.parent_id.substring(2)).toString();
          sessionStorage.setItem('po', orderId)
          navigate('/management/order/detail/' + orderId)
          break

        // Redirect kehalaman so detail
        case 'sales-order':
          let soId = parseInt(item.parent_id.substring(2)).toString();
          sessionStorage.setItem('id', soId)
          navigate('/management/so/detail/' + soId)
          break

        // Redirect kehalaman order detail
        case 'order':
          let oId = parseInt(item.parent_id.substring(2)).toString();
          sessionStorage.setItem('id', oId)
          navigate('/management/order/detail/' + oId)
          break

        // Redirect kehalaman sip detail
        case 'sip':
          let sipId = parseInt(item.parent_id.substring(2)).toString();
          sessionStorage.setItem('SI_ID', sipId)
          navigate('/management/sip/detail/' + sipId)
          break

        // Redirect kehalaman contract detail
        case 'contract':
          let kId = parseInt(item.parent_id.substring(2)).toString();
          sessionStorage.setItem('kid', kId);
          navigate('/management/kontrak/detail/' + kId);
          break

        default:
          break
      }
    } catch (error: any) {
      swal({title: 'Error', text: error?.message ?? 'error'})
    }
  }

  useEffect(() => {
    handleFetchNotifications()
    handleFetchUnreadNotifications()
  }, [])

  const [loadingRead, setLoadingRead] = useState(false)
  const markAllAsRead = async () => {
    setLoadingRead(true)
    try{
      await markAllNotificationAsRead(
        localStorage.getItem('credential') || '',
        {
          ids: unreadNotifications.map((item: any) => item.id)
        }
      )
      handleFetchNotifications()
      handleFetchUnreadNotifications()
      dispatch(setAmount(0))
      dispatch(setUnreadNotifications([]))
    }catch(e: any){
      swal({
        title: "Error",
        text: e.response?.data?.message || e.response?.message,
        icon: "error"
      })
    }
    setLoadingRead(false)
  }

  return (
    <>
      <PageTitle>{t('NOTIFICATIONS.TITLE')}</PageTitle>
      <StyledCard>
        <StyledContainer>
          <StyledContent>
            <div className='py-5'>
              <StyledHeader>
                  <SearchInput />
              </StyledHeader>
            </div>

            <StyledBody style={{padding: 0}}>
              {
                unreadNotifications.length > 0 && <>
                  <div className="d-flex justify-content-end p-4 align-items-center">
                    <span>Total Notifikasi ({ unreadNotifications.length })</span>
                    <button disabled={loadingRead} type='button' onClick={() => markAllAsRead()} className='ms-2 btn btn-link text-primary p-0'>{ 
                      loadingRead ? <i className='fas fa-spin fa-spinner'></i> : t('mark-as-read')
                    }</button>
                  </div>
                </>
              }
              {notifications.length === 0 ? (
                <EmptyListSection />
              ) : (
                <StyledList>
                  {notifications?.map((item, index) => {
                    return (
                      <ListItem key={index} item={item} onClick={() => handleListItemClick(item)} />
                    )
                  })}
                  {notifications.length > 0 && (
                    <StyledPagination className='bg-white'>
                      <Pagination paginate={paginateNumber} test={pagination?.links} />
                    </StyledPagination>
                  )}
                </StyledList>
              )}
            </StyledBody>
          </StyledContent>

        </StyledContainer>

      </StyledCard>
    </>
  )
}
